import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DownOutlined } from '@ant-design/icons';
import PopularMakesGrid from './PopularMakesGrid';
import PopularCarsList from './PopularCarsList';
import './BuyMegaMenu.css';

/* ── Desktop Panel ────────────────────────────────────────────── */
const DesktopPanel: React.FC<{ onNavigate: (href: string) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();

  const browseLinks = [
    { label: t('nav.buy_menu.all_vehicles'), href: '/mysearch' },
    { label: t('nav.buy_menu.new_cars'),     href: '/mysearch?condition=NEW' },
    { label: t('nav.buy_menu.used_cars'),    href: '/mysearch?condition=USED' },
  ];

  const budgetLinks = [
    { label: t('nav.buy_menu.budget_under_10k'), href: '/mysearch?priceTo=10000' },
    { label: t('nav.buy_menu.budget_10_20k'),    href: '/mysearch?priceFrom=10000&priceTo=20000' },
    { label: t('nav.buy_menu.budget_20_50k'),    href: '/mysearch?priceFrom=20000&priceTo=50000' },
    { label: t('nav.buy_menu.budget_luxury'),    href: '/mysearch?priceFrom=50000' },
  ];

  const categories = [
    { label: t('nav.buy_menu.category_suv'),       href: '/mysearch?bodyType=SUV',       img: '/images/categories/SUV.png' },
    { label: t('nav.buy_menu.category_sedan'),     href: '/mysearch?bodyType=SEDAN',     img: '/images/categories/Sedan.png' },
    { label: t('nav.buy_menu.category_hatchback'), href: '/mysearch?bodyType=HATCHBACK', img: '/images/categories/Hatchback.png' },
    { label: t('nav.buy_menu.category_coupe'),     href: '/mysearch?bodyType=COUPE',     img: '/images/categories/Coup.png' },
    { label: t('nav.buy_menu.category_pickup'),    href: '/mysearch?bodyType=PICKUP',    img: '/images/categories/Pickup.png' },
    { label: t('nav.buy_menu.category_cabriolet'), href: '/mysearch?bodyType=CABRIOLET', img: '/images/categories/Cabriolet.png' },
    { label: t('nav.buy_menu.category_minivan'),   href: '/mysearch?bodyType=MINIVAN',   img: '/images/categories/Minivan.png' },
  ];

  return (
    <div className="bmm-panel">
      <div className="bmm-inner">
        {/* Column 1 – Browse + Budget */}
        <div className="bmm-col">
          <div className="bmm-col-title">{t('nav.buy_menu.browse_vehicles')}</div>
          <ul className="bmm-link-list">
            {browseLinks.map(l => (
              <li key={l.href}>
                <a className="bmm-link" href="#" onClick={e => { e.preventDefault(); onNavigate(l.href); }}>{l.label}</a>
              </li>
            ))}
          </ul>

          <div className="bmm-divider" />

          <div className="bmm-col-title">{t('nav.buy_menu.by_budget')}</div>
          <ul className="bmm-link-list">
            {budgetLinks.map(l => (
              <li key={l.href}>
                <a className="bmm-link" href="#" onClick={e => { e.preventDefault(); onNavigate(l.href); }}>{l.label}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 2 – Popular Makes */}
        <div className="bmm-col">
          <div className="bmm-col-title">{t('nav.buy_menu.popular_makes')}</div>
          <PopularMakesGrid />
        </div>

        {/* Column 3 – Categories */}
        <div className="bmm-col">
          <div className="bmm-col-title">{t('nav.buy_menu.categories')}</div>
          <ul className="bmm-cat-list">
            {categories.map(c => (
              <li key={c.href}>
                <a className="bmm-cat-link" href="#" onClick={e => { e.preventDefault(); onNavigate(c.href); }}>
                  <img className="bmm-cat-img" src={c.img} alt={c.label} />
                  {c.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 4 – Popular Cars */}
        <div className="bmm-col">
          <div className="bmm-col-title">{t('nav.buy_menu.popular_cars')}</div>
          <PopularCarsList />
        </div>
      </div>

      {/* Footer bar */}
      <div className="bmm-footer">
        <div className="bmm-footer-inner">
          <a className="bmm-footer-link" href="#" onClick={e => { e.preventDefault(); onNavigate('/mysearch?condition=NEW'); }}>{t('nav.buy_menu.new_arrivals')}</a>
          <a className="bmm-footer-link" href="#" onClick={e => { e.preventDefault(); onNavigate('/mysearch?priceTag=GOOD_PRICE'); }}>{t('nav.buy_menu.good_price_deals')}</a>
          <a className="bmm-footer-cta" href="#" onClick={e => { e.preventDefault(); onNavigate('/mysearch'); }}>{t('nav.buy_menu.browse_all_listings')}</a>
        </div>
      </div>
    </div>
  );
};

/* ── Mobile accordion section ─────────────────────────────────── */
const MobileSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mob-nav-group">
      <button className="mob-nav-group-btn" onClick={() => setOpen(p => !p)}>
        {title}
        <DownOutlined className={`mob-nav-chevron ${open ? 'mob-nav-chevron-open' : ''}`} style={{ fontSize: 10 }} />
      </button>
      {open && <div className="mob-nav-group-content">{children}</div>}
    </div>
  );
};

/* ── Mobile View ──────────────────────────────────────────────── */
const MobileView: React.FC<{ onNavigate: (href: string) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();

  const browseLinks = [
    { label: t('nav.buy_menu.all_vehicles'), href: '/mysearch' },
    { label: t('nav.buy_menu.new_cars'),     href: '/mysearch?condition=NEW' },
    { label: t('nav.buy_menu.used_cars'),    href: '/mysearch?condition=USED' },
  ];

  const budgetLinks = [
    { label: t('nav.buy_menu.budget_under_10k'), href: '/mysearch?priceTo=10000' },
    { label: t('nav.buy_menu.budget_10_20k'),    href: '/mysearch?priceFrom=10000&priceTo=20000' },
    { label: t('nav.buy_menu.budget_20_50k'),    href: '/mysearch?priceFrom=20000&priceTo=50000' },
    { label: t('nav.buy_menu.budget_luxury'),    href: '/mysearch?priceFrom=50000' },
  ];

  const categories = [
    { label: t('nav.buy_menu.category_suv'),       href: '/mysearch?bodyType=SUV' },
    { label: t('nav.buy_menu.category_sedan'),     href: '/mysearch?bodyType=SEDAN' },
    { label: t('nav.buy_menu.category_hatchback'), href: '/mysearch?bodyType=HATCHBACK' },
    { label: t('nav.buy_menu.category_coupe'),     href: '/mysearch?bodyType=COUPE' },
    { label: t('nav.buy_menu.category_pickup'),    href: '/mysearch?bodyType=PICKUP' },
    { label: t('nav.buy_menu.category_cabriolet'), href: '/mysearch?bodyType=CABRIOLET' },
    { label: t('nav.buy_menu.category_minivan'),   href: '/mysearch?bodyType=MINIVAN' },
  ];

  return (
    <div className="mob-nav-sub">
      <MobileSection title={t('nav.buy_menu.browse_vehicles')}>
        {browseLinks.map(l => (
          <a key={l.href} className="mob-nav-sub-link" href="#" onClick={e => { e.preventDefault(); onNavigate(l.href); }}>{l.label}</a>
        ))}
      </MobileSection>
      <MobileSection title={t('nav.buy_menu.by_budget')}>
        {budgetLinks.map(l => (
          <a key={l.href} className="mob-nav-sub-link" href="#" onClick={e => { e.preventDefault(); onNavigate(l.href); }}>{l.label}</a>
        ))}
      </MobileSection>
      <MobileSection title={t('nav.buy_menu.categories')}>
        {categories.map(c => (
          <a key={c.href} className="mob-nav-sub-link" href="#" onClick={e => { e.preventDefault(); onNavigate(c.href); }}>{c.label}</a>
        ))}
      </MobileSection>
    </div>
  );
};

/* ── Main Export ──────────────────────────────────────────────── */
interface BuyMegaMenuProps {
  mobile?: boolean;
}

const BuyMegaMenu: React.FC<BuyMegaMenuProps> = ({ mobile = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavigate = (href: string) => {
    setOpen(false);
    navigate(href);
  };

  if (mobile) {
    return (
      <li className="mob-nav-item">
        <button className="mob-nav-trigger" onClick={() => setOpen(p => !p)}>
          {t('nav.buy')}
          <DownOutlined className={`mob-nav-chevron ${open ? 'mob-nav-chevron-open' : ''}`} />
        </button>
        {open && <MobileView onNavigate={handleNavigate} />}
      </li>
    );
  }

  return (
    <li ref={containerRef} className="nav-item bmm-nav-item">
      <a
        className="nav-link bmm-trigger"
        href="#"
        onClick={e => { e.preventDefault(); setOpen(p => !p); }}
      >
        {t('nav.buy')}
        <DownOutlined className={`bmm-chevron ${open ? 'bmm-chevron-open' : ''}`} />
      </a>

      {open && <DesktopPanel onNavigate={handleNavigate} />}
    </li>
  );
};

export default BuyMegaMenu;
